import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/auth";
import { useParams } from "react-router-dom";

interface AttendanceRecord {
  id: string;
  school_id: string;
  student_id: string;
  date: string;
  period?: number;
  status: "present" | "absent" | "late" | "excused" | "absent_with_reason";
  notes?: string;
  recorded_by?: string;
  recorded_at: string;
}

export const useAllAttendanceRecords = (
  all: boolean = true,
  startDate?: string,
  endDate?: string,
  grade: string = "all",
  section: string = "all"
) => {
  const [attendanceRecords, setAttendanceRecords] = useState<
    AttendanceRecord[]
  >([]);
  const [loading, setLoading] = useState(false);
  const { userProfile } = useAuth();
  const { schoolId: schooIdParam } = useParams();
  const schoolId = schooIdParam || userProfile?.school_id;

  const fetchAllAttendanceRecords = useCallback(async () => {
    // if (!user) {
    //   return;
    // }

    setLoading(true);

    try {
      const { data, error } = await supabase
        .from("attendance_records")
        .select(
          `*, students!student_id (
  id, grade, section
    )`
        )
        .order("date", { ascending: false })
        .eq("school_id", schoolId)
        .gte(startDate ? "date" : "", startDate || "")
        .lte(endDate ? "date" : "", endDate || "");

      if (error) {
        console.error(
          "useAllAttendanceRecords: Error fetching attendance records:",
          error
        );
        throw error;
      }

      let typedData = (data || []).map((record) => ({
        ...record,
        status: record.status as
          | "present"
          | "absent"
          | "absent_with_reason"
          | "late"
          | "excused",
      }));

      // If 'stage' is not a property, use a valid property like 'grade_id' or remove this filter
      // Example using 'grade_id' (replace with the correct property as needed):
      if (section && section !== "all") {
        typedData = typedData.filter(
          (record) => record.students?.section === section
        );
      }

      if (grade && grade !== "all") {
        typedData = typedData.filter(
          (record) => record.students?.grade === grade
        );
      }

      setAttendanceRecords(typedData);
    } catch (error) {
      console.error(
        "useAllAttendanceRecords: Error fetching attendance records:",
        error
      );
      setAttendanceRecords([]);
    } finally {
      setLoading(false);
    }
  }, [endDate, grade, schoolId, section, startDate]);

  useEffect(() => {
    fetchAllAttendanceRecords();
  }, [endDate, startDate, section, grade, fetchAllAttendanceRecords]);

  return {
    attendanceRecords,
    loading,
    refetch: fetchAllAttendanceRecords,
  };
};
